import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../lib/confirm';
import { FileText, Download, Check, Undo2 } from 'lucide-react';
import { request, api } from '../api/client';
import { Table, Skeleton, Result, Button, Input, Modal, useApp } from '../ui';
import { Pill } from './TwDataTable';
import { formatIst } from '../lib/time';
import { previewPdf } from '../lib/pdf';
import { usePermissionChecker } from '../store/auth';
import { inr2 } from '../lib/format';

const STATUS_PILL: Record<string, string> = { POSTED: 'green', DRAFT: 'gold', CANCELLED: 'red' };
const LEDGER_PILL: Record<string, string> = { CREDIT_NOTE: 'green', DEBIT_NOTE: 'red' };

/**
 * The canonical credit / debit-note view — a single dialog every surface deep-links to (the retailer ledger,
 * order detail, invoice detail, return detail, credit-note register). It resolves every link the note carries
 * (retailer, invoice, order, return), shows the reversal audit when the note was voided, and the ledger
 * movements it produced (the CREDIT_NOTE credit that granted the advance, and any compensating DEBIT_NOTE that
 * removed it on a reversal) — so a note is traceable from anywhere. `id` null → closed.
 *
 * A DIALOG, not a side drawer (it was a 480px drawer until 2026-09-14): a note is a settled document you read,
 * not something worked alongside the list behind it. At 480px the four-column ledger table could not fit, so
 * Debit/Credit scrolled off sideways; here the tax split and the links sit side by side, the ledger gets the
 * full width, and the actions live in the footer.
 *
 * For a TURNOVER_BONUS note (a standalone financial incentive, no return/invoice), it also carries the
 * maker-checker actions: approve a DRAFT → post it, or reverse a POSTED one. `onChanged` lets the opener (the
 * register) refetch after either. Both actions are permission-gated.
 */
export function CreditNoteModal({ id, onClose, onChanged }: { id: string | null; onClose: () => void; onChanged?: () => void }) {
  const { message } = useApp();
  const qc = useQueryClient();
  const hasPerm = usePermissionChecker();
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');

  const { data: n, isLoading, isError } = useQuery({
    queryKey: ['credit-note', id],
    queryFn: () => request<any>({ url: `/credit-notes/${id}` }),
    enabled: !!id,
  });

  const isCredit = n?.noteType === 'CREDIT';
  const cancelled = n?.status === 'CANCELLED';
  const isBonus = n?.source === 'TURNOVER_BONUS';

  const refresh = () => { qc.invalidateQueries({ queryKey: ['credit-note', id] }); onChanged?.(); };
  const approve = useConfirmedMutation({ title: "Approve this bonus credit note?", content: "It posts to the retailer’s ledger as a credit they can set against future invoices.", okText: "Yes, approve" }, {
    mutationFn: () => api.post(`/credit-notes/${id}/approve-bonus`),
    onSuccess: () => { message.success('Bonus approved & posted to the retailer ledger'); refresh(); },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not approve the bonus'),
  });
  const reverse = useConfirmedMutation({ title: "Reverse this bonus credit note?", content: "A compensating debit note is posted against the retailer’s ledger. The original credit note stays on record.", okText: "Yes, reverse", danger: true }, {
    mutationFn: () => api.post(`/credit-notes/${id}/reverse-bonus`, { reason: reverseReason.trim() }),
    onSuccess: () => { message.success('Bonus reversed'); setReverseOpen(false); setReverseReason(''); refresh(); },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not reverse the bonus'),
  });

  const openPdf = async () => {
    try {
      await previewPdf(() => api.get(`/credit-notes/${id}/pdf`, { responseType: 'blob' }));
    } catch {
      message.error('Failed to load the note PDF');
    }
  };

  // Turnover-bonus maker-checker actions (permission-gated).
  const canApprove = !!n && isBonus && n.status === 'DRAFT' && hasPerm('CREDIT_NOTE.BONUS.APPROVE');
  const canReverse = !!n && isBonus && n.status === 'POSTED' && hasPerm('CREDIT_NOTE.BONUS.REVERSE');
  const footer = n && (isCredit || canApprove || canReverse) ? (
    <>
      {isCredit && <Button icon={<Download size={16} />} onClick={openPdf}>Download credit note PDF</Button>}
      {canReverse && <Button variant="danger" icon={<Undo2 size={16} />} onClick={() => setReverseOpen(true)}>Reverse bonus</Button>}
      {canApprove && (
        <Button variant="primary" icon={<Check size={16} />} loading={approve.isPending} onClick={() => approve.mutate()}>
          Approve &amp; post {inr2(n.amount)}
        </Button>
      )}
    </>
  ) : undefined;

  return (
    <>
      <Modal
        open={!!id}
        onOpenChange={(o) => { if (!o) onClose(); }}
        width={820}
        title={
          <span className="inline-flex items-center gap-2">
            <FileText size={16} />
            {n ? `${isCredit ? 'Credit' : 'Debit'} Note ${n.noteNo}` : 'Note'}
          </span>
        }
        footer={footer}
      >
        {isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
        {isError && <Result status="404" title="Note not found" subTitle="It may have been removed, or you may not have access." />}
        {n && (
          <div className="space-y-4">
            {/* Header: status + headline amount */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="type-metric text-content">{inr2(n.amount)}</div>
                <div className="text-xs text-subtle">{n.noteDate ? String(n.noteDate).substring(0, 10) : '—'}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Pill color={STATUS_PILL[n.status] ?? 'default'}>{cancelled ? 'Reversed' : String(n.status ?? '').toLowerCase()}</Pill>
                {isBonus && <Pill color="blue">Turnover bonus</Pill>}
              </div>
            </div>

            {/* Draft bonus banner — not yet posted to the ledger; awaiting a checker's approval. */}
            {isBonus && n.status === 'DRAFT' && (
              <div className="rounded-control border border-warning-500/25 bg-warning-500/10 px-3 py-2 text-xs text-content">
                This turnover bonus is a <span className="font-semibold">draft</span> — it has not yet been posted to the retailer's ledger. Approving it credits their account (advance-in-credit).
              </div>
            )}

            {/* Reversal banner — a CANCELLED note was voided in error-correction; keep the trail visible. */}
            {cancelled && (
              <div className="rounded-control border border-danger-600/25 bg-danger-600/10 px-3 py-2 text-xs text-content">
                This {isBonus ? 'bonus' : 'credit note'} was <span className="font-semibold">reversed</span>
                {n.reversedAt ? ` on ${formatIst(n.reversedAt)}` : ''}{n.reversedByName ? ` by ${n.reversedByName}` : ''}.
                {n.reversalReason ? <div className="mt-0.5 text-muted">Reason: {n.reversalReason}</div> : null}
                <div className="mt-0.5 text-muted">The advance-in-credit it granted was removed by a compensating debit; {isBonus ? 'a corrected bonus can be re-issued.' : 'a corrected note can be re-issued from the return.'}</div>
              </div>
            )}

            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
              {/* Tax split */}
              <div className="rounded-control border border-line bg-surface-2 px-3 py-2 text-sm">
                <Row k={isBonus ? 'Incentive value' : 'Taxable value'} v={inr2(n.taxableAmt)} />
                {Number(n.cgstAmt) > 0 && <Row k="CGST" v={inr2(n.cgstAmt)} />}
                {Number(n.sgstAmt) > 0 && <Row k="SGST" v={inr2(n.sgstAmt)} />}
                {Number(n.igstAmt) > 0 && <Row k="IGST" v={inr2(n.igstAmt)} />}
                {isBonus && <Row k="GST" v="Not applicable (financial credit)" />}
                <div className="mt-1 border-t border-line pt-1"><Row k="Total" v={inr2(n.amount)} bold /></div>
              </div>

              {/* Linked documents — trace to everywhere this note connects. */}
              <div className="rounded-control border border-line bg-surface px-3 py-2 text-sm">
                <div className="mb-1 type-overline text-subtle">Linked to</div>
                <LinkRow k="Retailer" text={n.retailerName ? `${n.retailerCode ? n.retailerCode + ' · ' : ''}${n.retailerName}` : `#${n.retailerId}`} />
                {/* The year the bonus was EARNED — its own field now; `financialYear` is the note's own FY
                    (from its date). An older server only sends `financialYear`, which then meant the earned year. */}
                {isBonus && <Row k="Kind" v={<>Turnover / volume bonus{(n.earnedFinancialYear ?? n.financialYear) ? <> · <span className="whitespace-nowrap">Earned in FY {n.earnedFinancialYear ?? n.financialYear}</span></> : null}</>} />}
                {n.returnId && <LinkRow k="Sales return" text={n.returnNo ?? `#${n.returnId}`} to={`/returns/${n.returnId}`} onNav={onClose} />}
                {n.invoiceId && <LinkRow k="Against invoice" text={n.invoiceNo ?? `#${n.invoiceId}`} to={`/billing/invoices/${n.invoiceId}`} onNav={onClose} />}
                {n.orderId && <LinkRow k="Order" text={n.orderNo ?? `#${n.orderId}`} to={`/orders/${n.orderId}`} onNav={onClose} />}
                {n.reason && <Row k="Reason" v={n.reason} />}
                {n.createdByName && <Row k="Issued by" v={`${n.createdByName}${n.createdAt ? ` · ${formatIst(n.createdAt)}` : ''}`} />}
              </div>
            </div>

            {/* Ledger trail: the credit granted + any reversal debit. */}
            {(n.ledger?.length ?? 0) > 0 && (
              <div>
                <div className="mb-1 type-overline text-subtle">Ledger movements</div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(l: any) => String(l.id)}
                  dataSource={n.ledger}
                  columns={[
                    { title: 'When', dataIndex: 'createdAt', render: (v: any) => <span className="text-xs">{formatIst(v)}</span> },
                    { title: 'Type', dataIndex: 'txnType', render: (v: string) => <Pill color={LEDGER_PILL[v] ?? 'default'}>{String(v ?? '').replace(/_/g, ' ').toLowerCase()}</Pill> },
                    { title: 'Debit', dataIndex: 'debit', align: 'right' as const, render: (v: any) => Number(v) > 0 ? <span className="text-danger-600">{inr2(v)}</span> : <span className="text-subtle">—</span> },
                    { title: 'Credit', dataIndex: 'credit', align: 'right' as const, render: (v: any) => Number(v) > 0 ? <span className="text-success-600">{inr2(v)}</span> : <span className="text-subtle">—</span> },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reverse-reason capture — a compensating debit is posted; the note stays for audit. */}
      <Modal
        open={reverseOpen}
        title="Reverse turnover bonus"
        onOpenChange={(o) => { if (!o) { setReverseOpen(false); setReverseReason(''); } }}
        footer={
          <>
            <Button variant="default" onClick={() => { setReverseOpen(false); setReverseReason(''); }}>Cancel</Button>
            <Button variant="danger" disabled={!reverseReason.trim()} loading={reverse.isPending} onClick={() => reverse.mutate()}>Reverse</Button>
          </>
        }
      >
        <p className="mb-2 text-sm text-muted">
          This posts a compensating debit that removes the advance-in-credit this bonus granted, and marks the
          note reversed (kept for audit). This can't be undone — a corrected bonus can be raised afresh.
        </p>
        <Input.TextArea rows={3} placeholder="Reason for reversal…" aria-label="Reason for reversal" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
      </Modal>
    </>
  );
}

function Row({ k, v, bold }: { k: string; v: any; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-muted">{k}</span>
      <span className={bold ? 'font-semibold text-content' : 'text-content'}>{v}</span>
    </div>
  );
}

function LinkRow({ k, text, to, onNav }: { k: string; text: string; to?: string; onNav?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-muted">{k}</span>
      {to
        ? <Link to={to} onClick={onNav} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{text}</Link>
        : <span className="text-content">{text}</span>}
    </div>
  );
}
